<script lang="ts" setup>
import type { AcceptableValue } from 'reka-ui'
import type { BasicRoleUser } from '~/models/User/BasicUser'
import type { GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { debounce } from '~/utils/functionUtils'
import useArrayUtils from '~/utils/arrayUtils'
import { UserRole } from '~/models/User/UserRole'
import { GroupRole } from '~/models/Group'
import useGroupsApi from '~/api/groupsApi'
import AuCombobox from '~/components/ui/AuCombobox.vue'

const { shade = 'dark', studentsOnly = false } = defineProps<{ shade?: 'light' | 'dark', studentsOnly?: boolean }>()
const { t } = useI18n()
const { searchUsers } = useGroupsApi()
const { truthy } = useArrayUtils()

const selectedMembers = defineModel<GroupMember[]>()
const searchQuery = ref('')

const { data, isLoading, isError } = useQuery({
  queryKey: computed(() => ['searchUsersAdd', searchQuery.value]),
  queryFn: () => searchUsers(searchQuery.value),
  select: data => data
    .map((member: BasicRoleUser) => ({
      ...member,
      groupRole: member.role === UserRole.Teacher ? GroupRole.Admin : GroupRole.Member
    })),
  enabled: computed(() => searchQuery.value.length >= 2),
  staleTime: Infinity,
})

const members = computed(() => data.value ? (studentsOnly ? data.value.filter(user => user.role === UserRole.Student) : data.value) : [])
const noOptions = computed(() => {
  if (isError.value)
    return t('groups.userSearch.error')
  if (isLoading.value)
    return t('groups.userSearch.searching')
  return t('groups.userSearch.noResults')
})

const handleSearch = debounce((value: string) => {
  searchQuery.value = value
}, 300)

const updateSelectedMembers = (ids: AcceptableValue[]) => {
  selectedMembers.value = ids
    .map<GroupMember | undefined>(id => members.value
      .concat(selectedMembers.value ?? [])
      .find(member => member.userId === id))
    .filter<GroupMember>(truthy)
}
</script>

<template>
  <AuCombobox
    :model-value="selectedMembers?.map((x) => x.userId) ?? []"
    :no-options="noOptions"
    :option-id="(member: GroupMember) => member.userId"
    :option-label="(member: GroupMember) => member.fullName"
    :options="members"
    :placeholder="studentsOnly ? t('assign.searchSchoolPlaceholder') : t('groups.userSearch.placeholder')"
    :reset-search-term-on-blur="false"
    :shade
    multiple
    open-on-click
    @search="handleSearch"
    @update:model-value="updateSelectedMembers"
  />
</template>
